/**
 * Account Profile — Group Recruiting Permission Toggle.
 *
 * Visibility: gated client-side by `UserProfileService.isA([...BST], true)`.
 * The `true` second arg tells isA() to ignore impersonation and check the
 * REAL viewer's role — same pattern as AddAdminFeeWaiverButton,
 * ResyncCorporateEmailButton, etc. on this page. This is the only way the
 * toggle remains visible to a BST admin who is impersonating another user
 * (the impersonated identity isn't BST, so a backend-driven gate would
 * hide it).
 *
 * State: reflects whether the TARGET user (the account being viewed) is in
 * system_settings.recruiting---group-recruiting-permitted. Pulled via
 * GET /users/:id/recruiting-permission.
 *
 * Toggle hits POST /users/:id/recruiting-permission. Optimistic UI rollback
 * on error.
 */

import React from 'react'
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit'
import { toast } from 'react-toastify'
import RecruitingMeetingService from './../../../../shared/services/RecruitingMeeting.service'
import UserProfileService from './../../../../shared/services/UserProfile.service'

const BST_ROLES = ['system-admin', 'agency-owner', 'internal-admin', 'internal-staff']

class RecruitingPermissionToggle extends React.Component {
  state = {
    loading: true,
    enabled: false,
    saving: false,
    viewerCanEdit: false,
  }

  // Same pattern as ResyncCorporateEmailButton / AddAdminFeeWaiverButton —
  // computed once from UserProfileService, ignoring any active impersonation.
  _isBst = () => BST_ROLES.some(role => UserProfileService.isA(role, true))

  _meetsRequirement = () => this._isBst() || this.state.viewerCanEdit

  componentDidMount() {
    this._mounted = true
    this._fetch()
  }

  componentWillUnmount() {
    this._mounted = false
  }

  componentDidUpdate(prevProps) {
    if (prevProps.userId !== this.props.userId) this._fetch()
  }

  _fetch = async () => {
    const { userId } = this.props
    if (!userId) return
    this.setState({ loading: true })
    try {
      const data = await RecruitingMeetingService.fetchRecruitingPermission(userId)
      if (!this._mounted) return
      this.setState({
        loading: false,
        enabled: !!(data && data.enabled),
        viewerCanEdit: !!(data && data.viewerCanEdit),
      })
    } catch (ex) {
      if (!this._mounted) return
      this.setState({ loading: false, enabled: false, viewerCanEdit: false })
    }
  }

  _toggle = async () => {
    const { userId } = this.props
    const { enabled, saving } = this.state
    if (saving || !userId) return
    const next = !enabled
    // Optimistic flip.
    this.setState({ enabled: next, saving: true })
    try {
      await RecruitingMeetingService.setRecruitingPermission(userId, next)
      if (!this._mounted) return
      this.setState({ saving: false })
      toast.success(next ? 'Group Recruiting access granted.' : 'Group Recruiting access removed.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } catch (ex) {
      if (!this._mounted) return
      // Roll back.
      this.setState({ enabled, saving: false })
      const status = ex && ex.response && ex.response.status
      toast.error(
        status === 403
          ? 'You are not authorized to change this setting.'
          : 'Failed to update Group Recruiting access. Please try again.',
        { position: toast.POSITION.TOP_RIGHT }
      )
    }
  }

  render() {
    const { loading, enabled, saving } = this.state
    if (loading && !this._isBst()) return null
    if (!loading && !this._meetsRequirement()) return null

    return (
      <MDBBtn
        color={enabled ? 'success' : 'secondary'}
        className='btn-block'
        onClick={this._toggle}
        disabled={saving || loading}
      >
        <MDBIcon icon={enabled ? 'check-circle' : 'times-circle'} className='mr-2' />
        {loading ? 'Group Recruiting…' : enabled ? 'Group Recruiting: Permitted' : 'Group Recruiting: Not Permitted'}
      </MDBBtn>
    )
  }
}

export default RecruitingPermissionToggle
